import { DateTime } from "luxon";

/** GET part/orders/overview - whole warehouse, nothing per person. */
export interface ILeadTime {
  seconds: number | null;
  count: number;
  previousSeconds: number | null;
  previousCount: number;
}

export interface IOrdersOverview {
  generatedAt: string;
  openOrders: number;
  openParts: number;
  toPick: number;
  toPack: number;
  labelled: number;
  overdue: number;
  dueToday: number;
  later: number;
  noDate: number;
  dispatchedToday: number;
  dispatchedSameTimeLastWeek: number;
  /** When the bars (before today) and speeds were worked out; null until the job has run. */
  snapshotAt: string | null;
  dispatchedByDay: { date: string; orders: number }[];
  pickTime: ILeadTime;
  packTime: ILeadTime;
  saleToDispatch: ILeadTime;
}

/** "45s", "12m", "3h 20m", "2d 4h" - a day here is 24 working hours (weekends are left out). */
export function formatDuration(seconds: number | null | undefined) {
  if (seconds == null) return "–";
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
}

export type Trend = "better" | "worse" | "same" | "none";

/** A time against last week's: under 5% either way is "about the same". Lower is better. */
export function timeTrend(t: ILeadTime): { trend: Trend; text: string } {
  if (t.seconds == null || t.previousSeconds == null || t.previousSeconds === 0) return { trend: "none", text: "No comparison yet" };
  const change = (t.seconds - t.previousSeconds) / t.previousSeconds;
  const pct = Math.round(Math.abs(change) * 100);
  if (pct < 5) return { trend: "same", text: "About the same as last week" };
  return change < 0 ? { trend: "better", text: `${pct}% faster than last week` } : { trend: "worse", text: `${pct}% slower than last week` };
}

/** "+6 on last week", "−3 on last week", "Same as last week". More dispatched is better. */
export function dispatchedTrend(today: number, lastWeek: number): { trend: Trend; text: string } {
  const diff = today - lastWeek;
  if (diff === 0) return { trend: "same", text: "Same as last week" };
  return diff > 0 ? { trend: "better", text: `+${diff} on last week` } : { trend: "worse", text: `−${-diff} on last week` };
}

/** Bar label for a UK date: "Today", otherwise the short weekday ("Mon"). */
export function dayLabel(date: string, now: DateTime) {
  const d = DateTime.fromISO(date, { zone: "Europe/London" });
  return d.hasSame(now.setZone("Europe/London"), "day") ? "Today" : d.toFormat("ccc");
}

/** Each segment's share of the bar, in percent; empty segments get nothing. */
export function shares(counts: number[]) {
  const total = counts.reduce((a, b) => a + b, 0);
  return counts.map(c => (total ? (c / total) * 100 : 0));
}

/** "as of 13:45" for the snapshot behind the bars and speeds; "no snapshot yet" until the job has run. */
export function snapshotLabel(snapshotAt: string | null) {
  if (!snapshotAt) return "no snapshot yet";
  return `as of ${DateTime.fromISO(snapshotAt, { zone: "utc" }).setZone("Europe/London").toFormat("HH:mm")}`;
}
